import { OutputResult, OutputOptions } from "./types";
import chalk from "chalk";

export class OutputFormatter {
  static format(result: OutputResult, options: OutputOptions): string {
    if (options.quiet && result.success) return "";

    // Use human-readable format when format is not explicitly 'json'
    if (options.format !== "json") {
      return this.formatHuman(result, options);
    }

    return JSON.stringify(result, null, options.verbose ? 2 : 0);
  }

  static formatHuman(result: OutputResult, options: OutputOptions): string {
    if (result.success) {
      // Handle success case - could be expanded later
      return JSON.stringify(result, null, options.verbose ? 2 : 0);
    } else {
      // Format error in human-readable way with chalk
      return this.formatHumanError(result, options);
    }
  }

  static formatHumanError(result: OutputResult, options: OutputOptions): string {
    if (!result.error) {
      return chalk.red("⚠️  Unknown error occurred");
    }

    const lines: string[] = [];

    // Error header with icon
    lines.push(chalk.red.bold("⚠️  Error: ") + chalk.white(result.error.message));

    // Error code if available
    if (result.error.code && result.error.code !== "UNKNOWN_ERROR") {
      lines.push(chalk.gray("   Code: ") + chalk.yellow(result.error.code));
    }

    // Error details if available
    if (result.error.details && typeof result.error.details === "object") {
      const details = result.error.details;

      // Show specific helpful details
      if (details.crunchName) {
        lines.push(chalk.gray("   Crunch: ") + chalk.cyan(details.crunchName));
      }
      if (details.modelId) {
        lines.push(chalk.gray("   Model: ") + chalk.cyan(details.modelId));
      }
      if (details.prizeFileName) {
        lines.push(chalk.gray("   Prize File: ") + chalk.cyan(details.prizeFileName));
      }
      if (details.suggestion) {
        lines.push(chalk.gray("   Suggestion: ") + chalk.blue(details.suggestion));
      }
    }

    // Show original error in verbose mode
    if (options.verbose && result.error.originalError) {
      lines.push(chalk.gray("   Original: ") + chalk.gray(result.error.originalError));
    }

    // Show timestamp
    if (result.metadata?.timestamp) {
      lines.push(
        chalk.gray("   Time: ") + chalk.gray(new Date(result.metadata.timestamp).toLocaleString())
      );
    }

    return lines.join("\n");
  }

  static formatError(error: any, verbose: boolean = false): OutputResult {
    const errorResult: OutputResult = {
      success: false,
      error: {
        code: error.code || "UNKNOWN_ERROR",
        message: error.message || String(error),
        details: error.details || (verbose ? error.stack || error : error.message || String(error)),
      },
      metadata: {
        timestamp: new Date().toISOString(),
        command: process.argv.slice(2).join(" "),
      },
    };

    // Add original error and full stack trace when verbose is enabled
    if (errorResult.error) {
      if (verbose && error.originalError) {
        errorResult.error.originalError = error.originalError;
      }

      if (verbose) {
        // Prioritize original stack trace, fall back to current error stack
        errorResult.error.stackTrace = error.originalStack || error.stack;
      }
    }

    return errorResult;
  }

  static formatSuccess<T>(data: T, command: string): OutputResult<T> {
    return {
      success: true,
      data,
      metadata: {
        timestamp: new Date().toISOString(),
        command,
      },
    };
  }
}
